import type { DocumentItem } from './model.js';

/**
 * What a key press means, independent of any framework.
 *
 * Putting the keyboard contract in core rather than in each renderer is what
 * makes React and Angular *structurally* identical here, instead of two
 * hand-written handlers that drift the first time one is edited.
 */
export type KeyIntent =
  | { type: 'move'; index: number; extend: boolean }
  | { type: 'open'; item: DocumentItem }
  | { type: 'toggleSelect'; item: DocumentItem }
  | { type: 'selectAll' }
  | { type: 'navigateUp' }
  | { type: 'clearSelection' }
  | null;

/** The subset of a keyboard event this needs; keeps core DOM-free. */
export interface KeyEventLike {
  key: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

export interface KeyContext {
  items: readonly DocumentItem[];
  activeIndex: number;
  /** Items per visual row: 1 for a list, N for a grid. */
  columns?: number;
  /** Rows to jump for PageUp/PageDown. */
  pageSize?: number;
}

/** Time after which type-ahead starts a fresh search string. */
export const TYPEAHEAD_RESET_MS = 700;

export interface KeyboardNavigator {
  resolve(event: KeyEventLike, context: KeyContext, now?: number): KeyIntent;
  reset(): void;
}

/**
 * Create a navigator. It owns only the type-ahead buffer; everything else is
 * derived from the context passed in on each call.
 */
export function createKeyboardNavigator(): KeyboardNavigator {
  let buffer = '';
  let lastAt = 0;

  return {
    reset() {
      buffer = '';
      lastAt = 0;
    },

    resolve(event, context, now = 0): KeyIntent {
      const { items, activeIndex } = context;
      const count = items.length;
      if (count === 0) return null;

      const columns = context.columns ?? 1;
      const page = (context.pageSize ?? 10) * columns;
      const extend = event.shiftKey === true;
      const active = items[activeIndex];

      const move = (index: number): KeyIntent => ({
        type: 'move',
        index: Math.max(0, Math.min(count - 1, index)),
        extend,
      });

      switch (event.key) {
        case 'ArrowDown':
          return move(activeIndex + columns);
        case 'ArrowUp':
          return move(activeIndex - columns);
        // In a single-column list the horizontal arrows are left to the browser.
        case 'ArrowRight':
          return columns === 1 ? null : move(activeIndex + 1);
        case 'ArrowLeft':
          return columns === 1 ? null : move(activeIndex - 1);
        case 'Home':
          return move(0);
        case 'End':
          return move(count - 1);
        case 'PageDown':
          return move(activeIndex + page);
        case 'PageUp':
          return move(activeIndex - page);
        case 'Enter':
          return active ? { type: 'open', item: active } : null;
        case ' ':
        case 'Spacebar':
          return active ? { type: 'toggleSelect', item: active } : null;
        case 'Backspace':
          return { type: 'navigateUp' };
        case 'Escape':
          return { type: 'clearSelection' };
        case 'a':
        case 'A':
          // Only the modified form is select-all; a bare "a" must fall through
          // to type-ahead, or no name containing an "a" is ever reachable.
          if (event.ctrlKey || event.metaKey) return { type: 'selectAll' };
          break;
        default:
          break;
      }

      // Type-ahead: a printable character jumps to the next matching name.
      if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return null;

      buffer = now - lastAt > TYPEAHEAD_RESET_MS ? event.key : buffer + event.key;
      lastAt = now;
      const needle = buffer.toLowerCase();

      // Start after the active row, so repeating a key cycles through matches.
      for (let step = 1; step <= count; step++) {
        const index = (activeIndex + step) % count;
        if (items[index]?.name.toLowerCase().startsWith(needle)) {
          return { type: 'move', index, extend: false };
        }
      }
      return null;
    },
  };
}

/** Keys the navigator consumes, so a renderer knows when to preventDefault. */
export function isHandledKey(event: KeyEventLike): boolean {
  const NAVIGATION = new Set([
    'ArrowDown',
    'ArrowUp',
    'ArrowRight',
    'ArrowLeft',
    'Home',
    'End',
    'PageDown',
    'PageUp',
    'Enter',
    ' ',
    'Spacebar',
    'Backspace',
  ]);
  if (NAVIGATION.has(event.key)) return true;
  if ((event.key === 'a' || event.key === 'A') && (event.ctrlKey || event.metaKey)) return true;
  return false;
}
